import AppsIcon from '@mui/icons-material/Apps'
import ArticleIcon from '@mui/icons-material/Article'
import CodeIcon from '@mui/icons-material/Code'
import ContactPageIcon from '@mui/icons-material/ContactPage'
import EditIcon from '@mui/icons-material/Edit'
import MapIcon from '@mui/icons-material/Map'
import MovieIcon from '@mui/icons-material/Movie'
import PaletteIcon from '@mui/icons-material/Palette'
import PersonIcon from '@mui/icons-material/Person'
import DemoIcon from '@mui/icons-material/Science'
import SmartphoneIcon from '@mui/icons-material/Smartphone'
import WebIcon from '@mui/icons-material/Web'
import { Box, Card, CardActionArea, CardContent, Container, Divider, Grid2 as Grid, List, ListItem, ListItemIcon, ListItemText, Paper, Typography } from '@mui/material'
import React from 'react'
import { Link } from 'react-router'

/**
 * Development Page Navigation Component
 * Provides links to all development related pages
 */
const DevPage = () => {
  // List of development pages
  const devPages = [
    {
      path: '/dev/demo',
      title: 'Demo Page',
      description: 'General demo page with various example components',
      icon: <DemoIcon color="primary" />,
    },
    {
      path: '/dev/comp',
      title: 'Demo Components',
      description: 'Component demonstration and testing page',
      icon: <CodeIcon color="primary" />,
    },
    {
      path: '/dev/user-card',
      title: 'User Card',
      description: 'User card component development and testing',
      icon: <PersonIcon color="primary" />,
    },
    {
      path: '/dev/contact-card',
      title: 'Contact Card',
      description: 'Contact card component development and testing',
      icon: <ContactPageIcon color="primary" />,
    },
    {
      path: '/dev/theme',
      title: 'Theme Preview',
      description: 'Preview Material UI components with different states for theme development',
      icon: <PaletteIcon color="primary" />,
    },
    {
      path: '/dev/editor',
      title: 'Tiptap Editor',
      description: 'Tiptap rich text editor component development and testing',
      icon: <EditIcon color="primary" />,
    },
    {
      path: '/dev/media',
      title: 'Media Components',
      description: 'ImageGallery and Video component demonstration and testing',
      icon: <MovieIcon color="primary" />,
    },
    {
      path: '/feature-dev-map',
      title: 'Map Feature Development',
      description: 'Map related features and component development',
      icon: <MapIcon color="primary" />,
    },
  ]

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Development Page Navigation
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Quick links to all development and testing related pages
        </Typography>
        <Divider sx={{ mb: 4 }} />

        <List>
          {devPages.map((page, index) => (
            <React.Fragment key={page.path}>
              <ListItem
                component={Link}
                to={page.path}
                sx={{
                  borderRadius: 1,
                  '&:hover': {
                    bgcolor: 'action.hover',
                    transform: 'translateY(-2px)',
                    transition: 'all 0.2s',
                  },
                  mb: 1,
                }}
              >
                <ListItemIcon>{page.icon}</ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="h6" color="primary">
                      {page.title}
                    </Typography>
                  }
                  secondary={page.description}
                />
              </ListItem>
              {index < devPages.length - 1 && <Divider variant="inset" component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography variant="h5" component="h2" gutterBottom>
            Related Resources
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardActionArea component={Link} to="/sitemap">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AppsIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Site Map</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  View complete site map with all accessible pages
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardActionArea component={Link} to="/demo">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <DemoIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Main Demo Page</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Access the main demo page showcasing various features
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardActionArea component="a" href="/demo.html" target="_blank">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <SmartphoneIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">App Demo</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  View interactive app demo interface with mobile device simulation
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardActionArea component="a" href="/Information_Architecture.html" target="_blank">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <ArticleIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">Information Architecture</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  View application information architecture documentation
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
          <Card>
            <CardActionArea component="a" href="/Information_Architecture_UI_Focus.html" target="_blank">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <WebIcon color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">UI Architecture</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  View UI-focused information architecture documentation
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>
    </Container>
  )
}

export default DevPage
